/**
 * Feature-intro registry.
 *
 * Maps a backend `FeatureIntroKey` (e.g. 'ADVISORY') to the client-side
 * content and enable action for its one-time intro modal. The backend
 * surfaces pending keys via `module/select` -> `pendingFeatureIntros`;
 * the trigger in `app-compat.ts` picks the first key that has an entry
 * here and shows its modal.
 *
 * Adding a future feature's intro is purely additive: add an entry keyed
 * by its FeatureIntroKey. No schema or framework changes required.
 */

import { enableAdvisory } from '@/services/api';
import { SetupWizardModal } from '@/components/modals/SetupWizardModal';

/** Props passed to a custom (multi-step) intro component. */
export interface FeatureIntroCustomProps {
  theme?: string;
  /** The component has already persisted its outcome server-side. */
  onComplete: () => void;
  /** User bailed — the caller dismisses the intro (generic dismiss endpoint). */
  onSkip: () => void;
}

export interface FeatureIntroEntry {
  /** Modal title (used by the standard confirm-modal rendering). */
  title: string;
  /** Primary (enable) button label. */
  confirmText?: string;
  /** Rich JSX body rendered inside the modal. */
  body?: () => React.ReactNode;
  /** Calls the feature-specific enable endpoint; resolves on success. */
  onEnable?: () => Promise<void>;
  /**
   * Custom component override for interactive intros (e.g. the setup wizard).
   * When set, it is rendered instead of the standard FeatureIntroModal and
   * owns its own flow; confirmText/body/onEnable are ignored.
   */
  component?: (props: FeatureIntroCustomProps) => React.ReactNode;
}

export const featureIntroRegistry: Record<string, FeatureIntroEntry> = {
  ADVISORY: {
    title: 'New: Advisory Board',
    confirmText: 'Enable Advisory Board',
    body: () => (
      <div>
        <div>
          Post temporary notices your officers review at shift start, such as
          parking exceptions, BOLOs, and seasonal changes, scoped to a
          client or site.
        </div>
        <div className="advisory-intro-card" aria-hidden="true">
          <div className="advisory-intro-card__chip">Parking Exception</div>
          <div className="advisory-intro-card__subject">Red sedan &mdash; visitor driveway, unit #14</div>
          <div className="advisory-intro-card__meta">Maple Grove HOA &middot; Building C</div>
          <div className="advisory-intro-card__expires">Auto-removes Jun 24, 5:00 PM</div>
        </div>
        <div style={{ marginTop: '0.75rem' }}>
          Advisories with an end date drop off the board automatically when
          they no longer apply, so there is nothing to clean up.
        </div>
        <div style={{ marginTop: '0.75rem' }}>
          Enable it now, or anytime later from <strong>Admin &rarr; Permissions</strong> by
          granting the Advisory permissions to your groups.
        </div>
      </div>
    ),
    onEnable: () => enableAdvisory(),
  },

  SETUP_WIZARD: {
    title: "Let's set up your Orna workspace",
    component: (props) => <SetupWizardModal {...props} />,
  },
};
